import React from "react";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Tooltip } from "primereact/tooltip";
import CountryDropdown from "~/components/countryDropDown";
import { countryPhoneCodes } from "~/utils/countries";
import type { CustomerAdminFormData } from "../../utils/formatters";

interface CustomerAdminFormProps {
  data: CustomerAdminFormData;
  onChange: (updates: Partial<CustomerAdminFormData>) => void;
  onBlur?: (fieldName: string) => void;
  errors: Record<string, string[]>;
  touchedFields?: Set<string>;
  disabled?: boolean;
}

/**
 * Step 2: Admin Information Form
 */
export const CustomerAdminForm: React.FC<CustomerAdminFormProps> = ({
  data,
  onChange,
  onBlur,
  errors,
  touchedFields = new Set(),
  disabled = false,
}) => {
  const handleFieldChange = (
    field: keyof CustomerAdminFormData,
    value: any,
  ) => {
    onChange({ [field]: value });
  };

  const handleFieldBlur = (field: keyof CustomerAdminFormData) => {
    if (onBlur) {
      onBlur(field);
    }
  };

  // Only show errors for touched fields
  const getFieldError = (fieldName: string) => {
    if (touchedFields.has(fieldName) && errors[fieldName]) {
      return errors[fieldName];
    }
    return undefined;
  };

  // Prepare phone code options for dropdown
  const phoneCodeOptions = countryPhoneCodes.map((item) => ({
    label: `${item.dial_code} ${item.name}`,
    value: item.dial_code.replace("+", ""), // Store without + sign
  }));

  return (
    <div className="formgrid grid">
      <div className="field col-12">
        <p className="sub-heading">Customer Admin</p>
      </div>

      <div className="field col-12 md:col-6">
        <label htmlFor="given_name" className="input-label mb-2">
          Given Name(s) *{" "}
          <span id="given-name-info" className="pi pi-info-circle" />
          <Tooltip
            target="#given-name-info"
            content="The person's first name or names they usually go by."
            position="right"
            className="custom-tooltip tooltip-custom-format"
          />
        </label>
        <InputText
          id="given_name"
          name="given_name"
          value={data.given_name}
          onChange={(e) => handleFieldChange("given_name", e.target.value)}
          onBlur={() => handleFieldBlur("given_name")}
          className={`w-full ${getFieldError("given_name") ? "p-invalid" : ""}`}
          disabled={disabled}
          placeholder="Enter given name(s)"
          autoComplete="given-name"
          tabIndex={1}
          aria-describedby={
            getFieldError("given_name") ? "given_name-error" : "given-name-info"
          }
          aria-invalid={!!getFieldError("given_name")}
          aria-required="true"
        />
        {getFieldError("given_name") && (
          <small id="given_name-error" className="p-error" role="alert">
            {getFieldError("given_name")[0]}
          </small>
        )}
      </div>

      <div className="field col-12 md:col-6">
        <label htmlFor="other_name" className="input-label mb-2">
          Other Name(s){" "}
          <span id="other-name-info" className="pi pi-info-circle" />
          <Tooltip
            target="#other-name-info"
            content="Additional names such as middle names or clan names."
            position="right"
            className="custom-tooltip tooltip-custom-format"
          />
        </label>
        <InputText
          id="other_name"
          name="other_name"
          value={data.other_name}
          onChange={(e) => handleFieldChange("other_name", e.target.value)}
          onBlur={() => handleFieldBlur("other_name")}
          className={`w-full ${getFieldError("other_name") ? "p-invalid" : ""}`}
          disabled={disabled}
          placeholder="Enter any other names such as middle names or clan names."
          autoComplete="additional-name"
          tabIndex={2}
          aria-describedby={
            getFieldError("other_name") ? "other_name-error" : "other-name-info"
          }
          aria-invalid={!!getFieldError("other_name")}
        />
        {getFieldError("other_name") && (
          <small id="other_name-error" className="p-error" role="alert">
            {getFieldError("other_name")[0]}
          </small>
        )}
      </div>

      <div className="field col-12 md:col-6">
        <label htmlFor="surname" className="input-label mb-2">
          Surname(s) <span id="surname-info" className="pi pi-info-circle" />
          <Tooltip
            target="#surname-info"
            content="The person's family name or last name."
            position="right"
            className="custom-tooltip tooltip-custom-format"
          />
        </label>
        <InputText
          id="surname"
          name="surname"
          value={data.surname}
          onChange={(e) => handleFieldChange("surname", e.target.value)}
          onBlur={() => handleFieldBlur("surname")}
          className={`w-full ${getFieldError("surname") ? "p-invalid" : ""}`}
          disabled={disabled}
          placeholder="Enter surname(s)"
          autoComplete="family-name"
          tabIndex={3}
          aria-describedby={
            getFieldError("surname") ? "surname-error" : "surname-info"
          }
          aria-invalid={!!getFieldError("surname")}
        />
        {getFieldError("surname") && (
          <small id="surname-error" className="p-error" role="alert">
            {getFieldError("surname")[0]}
          </small>
        )}
      </div>

      <div className="field col-12 md:col-6">
        <label htmlFor="preferred_name" className="input-label mb-2">
          Preferred Name(s)
        </label>
        <InputText
          id="preferred_name"
          name="preferred_name"
          value={data.preferred_name}
          onChange={(e) => handleFieldChange("preferred_name", e.target.value)}
          onBlur={() => handleFieldBlur("preferred_name")}
          className={`w-full ${getFieldError("preferred_name") ? "p-invalid" : ""}`}
          disabled={disabled}
          placeholder="Preferred name or nickname"
          autoComplete="nickname"
          tabIndex={4}
          aria-describedby={
            getFieldError("preferred_name") ? "preferred_name-error" : undefined
          }
          aria-invalid={!!getFieldError("preferred_name")}
        />
        {getFieldError("preferred_name") && (
          <small id="preferred_name-error" className="p-error" role="alert">
            {getFieldError("preferred_name")[0]}
          </small>
        )}
      </div>

      <div className="field col-12 md:col-6">
        <label htmlFor="country" className="input-label mb-2">
          Country * <span id="country-info" className="pi pi-info-circle" />
          <Tooltip
            target="#country-info"
            content="The country where the customer admin is located."
            position="right"
            className="custom-tooltip tooltip-custom-format"
          />
        </label>
        <CountryDropdown
          id="country"
          name="country"
          value={data.country}
          onChange={(e) => {
            const twoCharCode = e.value?.code || "";
            handleFieldChange("country", twoCharCode);
          }}
          onBlur={() => handleFieldBlur("country")}
          error={
            getFieldError("country")
              ? { message: getFieldError("country")![0] }
              : undefined
          }
          disabled={disabled}
          tabIndex={5}
          aria-describedby={
            getFieldError("country") ? "country-error" : "country-info"
          }
          aria-required="true"
        />
        {getFieldError("country") && (
          <small id="country-error" className="p-error" role="alert">
            {getFieldError("country")[0]}
          </small>
        )}
      </div>

      <div className="field col-12 md:col-6">
        <label htmlFor="contact_email" className="input-label mb-2">
          Contact Email *{" "}
          <span id="contact-email-info" className="pi pi-info-circle" />
          <Tooltip
            target="#contact-email-info"
            content="Email address for contacting the customer admin."
            position="right"
            className="custom-tooltip tooltip-custom-format"
          />
        </label>
        <InputText
          id="contact_email"
          name="contact_email"
          value={data.contact_email}
          onChange={(e) => handleFieldChange("contact_email", e.target.value)}
          onBlur={() => handleFieldBlur("contact_email")}
          className={`w-full ${getFieldError("contact_email") ? "p-invalid" : ""}`}
          disabled={disabled}
          placeholder="Enter email address"
          type="email"
          autoComplete="email"
          tabIndex={6}
          aria-describedby={
            getFieldError("contact_email")
              ? "contact_email-error"
              : "contact-email-info"
          }
          aria-invalid={!!getFieldError("contact_email")}
          aria-required="true"
        />
        {getFieldError("contact_email") && (
          <small id="contact_email-error" className="p-error" role="alert">
            {getFieldError("contact_email")[0]}
          </small>
        )}
      </div>

      <div className="field col-12 md:col-6">
        <label htmlFor="phone_code" className="input-label mb-2">
          Phone number <span id="phone-info" className="pi pi-info-circle" />
          <Tooltip
            target="#phone-info"
            content="Phone number including country code for contacting the customer admin."
            position="right"
            className="custom-tooltip tooltip-custom-format"
          />
        </label>
        <div className="flex gap-2">
          <Dropdown
            id="phone_code"
            name="phone_code"
            value={data.phone_code || null}
            onChange={(e) => handleFieldChange("phone_code", e.value || "")}
            onBlur={() => handleFieldBlur("phone_code")}
            options={phoneCodeOptions}
            optionLabel="label"
            optionValue="value"
            className={`w-full ${getFieldError("phone_code") ? "p-invalid" : ""}`}
            disabled={disabled}
            placeholder="Country code"
            appendTo={document.body}
            tabIndex={7}
            aria-describedby={
              getFieldError("phone_code") ? "phone_code-error" : "phone-info"
            }
            aria-invalid={!!getFieldError("phone_code")}
            aria-label="Phone country code"
          />
          <InputText
            id="phone_number"
            name="phone_number"
            value={data.phone_number}
            onChange={(e) => handleFieldChange("phone_number", e.target.value)}
            onBlur={() => handleFieldBlur("phone_number")}
            className={`w-full ${getFieldError("phone_number") ? "p-invalid" : ""}`}
            disabled={disabled}
            placeholder="Enter phone number"
            type="tel"
            autoComplete="tel-national"
            tabIndex={8}
            aria-describedby={
              getFieldError("phone_number")
                ? "phone_number-error"
                : "phone-info"
            }
            aria-invalid={!!getFieldError("phone_number")}
            aria-label="Phone number"
          />
        </div>
        {getFieldError("phone_code") && (
          <small id="phone_code-error" className="p-error" role="alert">
            {getFieldError("phone_code")[0]}
          </small>
        )}
        {getFieldError("phone_number") && (
          <small id="phone_number-error" className="p-error" role="alert">
            {getFieldError("phone_number")[0]}
          </small>
        )}
      </div>
    </div>
  );
};
